<template>
  <div class="container">
    <table>
      <thead>
        <tr>
          <th/>
          <th v-for="(section, i) in entries" :key="section">{{ section.substr(0, 6) }}</th>
        </tr>
      </thead>
      <tbody v-if="snapshots">
        <tr v-for="(snap, snapIndex) in snapshots" :key="snap.id">
          <th>{{ snap.title.substring(0, 8) }}</th>
          <td v-for="(section, sectionIndex) in entries" v-if="type === 'scores'" :key="sectionIndex">{{ (snap.scores[sectionIndex] === 0) ? 'N/A' : snap.scores[sectionIndex].toFixed(2) }}</td>
          <td v-for="(section, sectionIndex) in entries" v-if="type === 'satisfaction'" :key="sectionIndex" :style="'background-color: ' + computeColor(snap.color[sectionIndex])">
            <span><fa :icon="computeIcon(snap.color[sectionIndex])" size="lg" fixed-width /></span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import Calculations from '~/utils/Calculations'
import axios from 'axios'

export default {
  name: 'ReportTable',
  mixins: [Calculations],

  props: {
    type: { type: String, required: true },
    evaluation: { type: Object, required: true },
    snapshots: { type: Array, required: true },
    entries: { type: Array, required: true }
  },

  data: () => ({
    satisfactionScores: [4, 3, 2, 1]
  }),

  methods: {
    getSnapshotsScores () {
      this.snapshots.forEach((snap, index, array) => {
        let computedScores = []
        let sections = JSON.parse(snap.snapshot)
        for (let i in sections) {
          if (typeof this.evaluation.data[0].children[i].children !== 'undefined') {
            const length = this.evaluation.data[0].children[i].children.length
            this.computeAllScores(sections[i], length)
            let scores = this.scores
            let avg = this.average(scores)
            this.clearScores()
            // array that contains average of scores by sections
            computedScores.push(avg)
          }
        }

        let chartData = []

        // Compute total
        chartData.push(this.average(computedScores))

        // Compute DailyActivity
        let clone1 = [...computedScores]
        let scoresDailyActivity = clone1.splice(0, 6)
        chartData.push(this.average(scoresDailyActivity))
        chartData = chartData.concat(scoresDailyActivity)

        // Compute SocialRole
        let clone2 = [...computedScores]
        let scoresSocialRole = clone2.splice(6)
        chartData.push(this.average(scoresSocialRole))
        chartData = chartData.concat(scoresSocialRole)

        snap.scores = chartData
      })
    },

    getSnapshotsSatisfaction () {
      let bigMatrix = []
      for (let i = 0; i < this.snapshots.length; i++) {
        const sections = JSON.parse(this.snapshots[i].snapshot)
        let singleData = []
        for (let index in sections) {
          if (typeof this.evaluation.data[0].children[index].children !== 'undefined') {
            let section = sections[index]
            let satisfaction = {
              really_satisfied: 0,
              satisfied: 0,
              unsatisfied: 0,
              really_unsatisfied: 0
            }
            let cpt = 0
            for (let i = 0; i < section.step3.value.length; i++) {
              if (section.step3.value[i] !== null && satisfaction.hasOwnProperty(section.step3.value[i].result)) {
                satisfaction[section.step3.value[i].result]++
                cpt++
              }
            }
            let data = Object.values(satisfaction)
            singleData.push({ data, cpt })
          }
        }
        let total = { data: [0, 0, 0, 0], cpt: 0 }
        for (let i = 0; i < singleData.length; i++) {
          total.data[0] += singleData[i].data[0]
          total.data[1] += singleData[i].data[1]
          total.data[2] += singleData[i].data[2]
          total.data[3] += singleData[i].data[3]
          total.cpt += singleData[i].cpt
        }

        let dailyActivity = { data: [0, 0, 0, 0], cpt: 0 }
        for (let i = 0; i < 6; i++) {
          dailyActivity.data[0] += singleData[i].data[0]
          dailyActivity.data[1] += singleData[i].data[1]
          dailyActivity.data[2] += singleData[i].data[2]
          dailyActivity.data[3] += singleData[i].data[3]
          dailyActivity.cpt += singleData[i].cpt
        }

        let socialRoles = { data: [0, 0, 0, 0], cpt: 0 }
        for (let i = 6; i < singleData.length; i++) {
          socialRoles.data[0] += singleData[i].data[0]
          socialRoles.data[1] += singleData[i].data[1]
          socialRoles.data[2] += singleData[i].data[2]
          socialRoles.data[3] += singleData[i].data[3]
          socialRoles.cpt += singleData[i].cpt
        }

        singleData.unshift(dailyActivity)
        singleData.unshift(total)
        singleData.splice(8, 0, socialRoles)
        bigMatrix.push(singleData)
      }
      for (let i = 0; i < this.snapshots.length; i++) {
        this.snapshots[i].color = []
        for (let j = 0; j < bigMatrix[0].length; j++) {
          let ponderateSatisfaction = 0
          for (let k = 0; k < bigMatrix[0][0].data.length; k++) {
            ponderateSatisfaction += this.satisfactionScores[k] * bigMatrix[i][j].data[k]
          }
          const avgSatisfaction = ponderateSatisfaction / bigMatrix[i][j].cpt
          this.snapshots[i].color.push(avgSatisfaction)
        }
      }
    },

    computeColor (score) {
      if (score < 2) {
        return 'darkred'
      } else if (score > 3) {
        return 'darkgreen'
      } else {
        return 'darkorange'
      }
    },

    computeIcon (score) {
      if (score < 2) {
        return 'frown'
      } else if (score > 3) {
        return 'smile'
      } else {
        return 'meh'
      }
    },

    loadScores () {
      if (this.type === 'scores') {
        this.getSnapshotsScores()
      } else if (this.type === 'satisfaction') {
        this.getSnapshotsSatisfaction()
      }
    },

    reload () {
      this.loadScores()
    }
  }
}
</script>

<style scoped>
  table {
    font-family: 'Open Sans', sans-serif;
    font-size: 14px;
    width: available;
    border-collapse: collapse;
    border: 3px solid #FFF;
    margin: 10px 10px 0 10px;
  }

  table th {
    text-transform: uppercase;
    text-align: left;
    background: rgb(13, 36, 84);
    color: #FFF;
    padding: 8px;
    min-width: 30px;
    border-right: 1px solid lightblue;
    border-bottom: 1px solid lightblue;
  }

  table span {
    color: white;
  }

  table td {
    text-align: center;
    padding: 8px;
    border-right: 1px solid rgb(13, 36, 84);
    border-bottom: 2px solid rgb(13, 36, 84);
  }
  table td:last-child {
    border-right: none;
  }
  table tbody tr:nth-child(2n) td {
    background: #D4D8F9;
  }
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>



// WEBPACK FOOTER //
// ../../../../resources/js/components/reports/ReportTable.vue